import { createLeadGenId } from "./types";
import defaultArtifacts from "./artifacts.json";

/** Reusable markdown note in the lead-gen artefact library. */
export interface LeadGenArtifact {
    id: string;
    name: string;
    markdown: string;
}

export const DEFAULT_LEAD_GEN_ARTIFACTS = defaultArtifacts as LeadGenArtifact[];

export function normalizeLeadGenArtifacts(artifacts: unknown): LeadGenArtifact[] {
    if (!Array.isArray(artifacts)) return [];

    const seen = new Set<string>();
    return artifacts.flatMap((item) => {
        if (!item || typeof item !== "object") return [];
        const row = item as Partial<LeadGenArtifact> & { artifact_id?: string };
        const name = String(row.name ?? "").trim();
        const markdown = String(row.markdown ?? "");
        if (!name && !markdown.trim()) return [];
        const id =
            (typeof row.id === "string" && row.id.trim()) ||
            (typeof row.artifact_id === "string" && row.artifact_id.trim()) ||
            createLeadGenId();
        if (seen.has(id)) return [];
        seen.add(id);
        return [{ id, name: name || "Untitled artefact", markdown }];
    });
}

export function normalizeArtifactIds(ids: unknown): string[] {
    if (!Array.isArray(ids)) return [];
    const seen = new Set<string>();
    return ids.flatMap((id) => {
        const value = String(id ?? "").trim();
        if (!value || seen.has(value)) return [];
        seen.add(value);
        return [value];
    });
}

export function cloneDefaultArtifacts(): LeadGenArtifact[] {
    return normalizeLeadGenArtifacts(DEFAULT_LEAD_GEN_ARTIFACTS).map((artifact) => ({ ...artifact }));
}

export function resolveArtifactsById(ids: string[] | undefined, library: LeadGenArtifact[]): LeadGenArtifact[] {
    const byId = new Map(library.map((artifact) => [artifact.id, artifact]));
    return normalizeArtifactIds(ids).flatMap((id) => {
        const artifact = byId.get(id);
        return artifact ? [{ ...artifact }] : [];
    });
}

export function formatArtifactsForPrompt(artifacts: LeadGenArtifact[]): string {
    const usable = normalizeLeadGenArtifacts(artifacts).filter((artifact) => artifact.markdown.trim());
    if (usable.length === 0) return "(none)";
    return usable.map((artifact) => `### ${artifact.name}\n\n${artifact.markdown.trim()}`).join("\n\n");
}

/** Template-selected artefacts first, then the rest of the library. */
export function formatArtifactsForGeneration(
    templateArtifacts: LeadGenArtifact[],
    library: LeadGenArtifact[] = []
): string {
    const primary = normalizeLeadGenArtifacts(templateArtifacts).filter((artifact) => artifact.markdown.trim());
    const primaryIds = new Set(primary.map((artifact) => artifact.id));
    const extra = normalizeLeadGenArtifacts(library).filter(
        (artifact) => artifact.markdown.trim() && !primaryIds.has(artifact.id)
    );

    if (primary.length === 0 && extra.length === 0) return "(none)";

    const blocks: string[] = [];
    if (primary.length > 0) {
        blocks.push(
            "Template artefacts (required). If these include successful outreach, match that voice and structure:",
            formatArtifactsForPrompt(primary)
        );
    }
    if (extra.length > 0) {
        blocks.push("Other artefacts in the library (use when relevant):", formatArtifactsForPrompt(extra));
    }
    return blocks.join("\n\n");
}

export function formatArtifactSummary(artifacts: LeadGenArtifact[]): string {
    const names = normalizeLeadGenArtifacts(artifacts)
        .map((artifact) => artifact.name.trim())
        .filter(Boolean);
    return names.join(" · ");
}

export function mergeArtifactLibraries(...groups: LeadGenArtifact[][]): LeadGenArtifact[] {
    const byId = new Map<string, LeadGenArtifact>();
    for (const group of groups) {
        for (const artifact of normalizeLeadGenArtifacts(group)) {
            if (!byId.has(artifact.id)) byId.set(artifact.id, artifact);
        }
    }
    return Array.from(byId.values()).toSorted((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );
}
